<script setup lang="ts">
import { useAppStore } from "@/store/app";
import { computeDominantColorFromImage } from "@/util/color";
import { getEffectiveBackgroundImageUrlForDevice } from "@/util/formStyleBackgroundImage";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();
const appStore = useAppStore();

const isMobile = computed(() => appStore.deviceMode === "mobile");
const messageStyle = computed(() => appStore.submitMessageStyle);

// Check if auto bg from image is enabled
const isAutoBgEnabled = computed(() =>
  Boolean((appStore.submitMessageStyle as any).autoBgFromImage)
);

const previewTextStyle = computed(() => {
  const style: any = {
    fontFamily: String(messageStyle.value.fontFamily || ""),
  };
  style.color = `#${messageStyle.value.color || "000000"}`;
  style.fontSize = `${messageStyle.value.fontSize}px`;
  style.fontWeight = isMobile.value
    ? (messageStyle.value as any).mobileFontWeight ?? messageStyle.value.fontWeight
    : messageStyle.value.fontWeight;
  const radius = Number((messageStyle.value as any).cornerRadius ?? 10);
  style.borderRadius = `${radius}px`;
  return style;
});

const messageContainerBoxStyle = computed(() => {
  const s: any = appStore.submitMessageStyle || {};
  const defaultPadding = { top: 48, right: 48, bottom: 48, left: 48 };
  const defaultMargin = { top: "auto", right: "auto", bottom: "auto", left: "auto" };

  const rawPad = isMobile.value ? s.mobilePadding || s.padding : s.padding;
  const pad = {
    top: rawPad?.top ?? defaultPadding.top,
    right: rawPad?.right ?? defaultPadding.right,
    bottom: rawPad?.bottom ?? defaultPadding.bottom,
    left: rawPad?.left ?? defaultPadding.left,
  };

  const rawMar = isMobile.value ? s.mobileMargin || s.margin : s.margin;
  const mar = {
    top: rawMar?.top ?? defaultMargin.top,
    right: rawMar?.right ?? defaultMargin.right,
    bottom: rawMar?.bottom ?? defaultMargin.bottom,
    left: rawMar?.left ?? defaultMargin.left,
  };

  const useAuto = Boolean(s.autoBgFromImage);
  const bgHex = `#${(isMobile.value ? s.mobileBgColor ?? s.bgColor : s.bgColor) || "FFFFFF"}`;
  const radius = Number((s as any).cornerRadius ?? 10);

  return {
    backgroundColor: useAuto ? "transparent" : bgHex,
    borderRadius: `${radius}px`,
    paddingTop: `${Number(pad.top)}px`,
    paddingRight: `${Number(pad.right)}px`,
    paddingBottom: `${Number(pad.bottom)}px`,
    paddingLeft: `${Number(pad.left)}px`,
    marginTop: mar.top === "auto" ? "auto" : `${Number(mar.top)}px`,
    marginRight: mar.right === "auto" ? "auto" : `${Number(mar.right)}px`,
    marginBottom: mar.bottom === "auto" ? "auto" : `${Number(mar.bottom)}px`,
    marginLeft: mar.left === "auto" ? "auto" : `${Number(mar.left)}px`,
    width: "fit-content",
    maxWidth: "384px",
  };
});

const messageContainerOverlayStyle = computed(() => {
  const s: any = appStore.submitMessageStyle || {};
  const useAuto = Boolean(s.autoBgFromImage);
  const rawHex = (isMobile.value ? s.mobileBgColor ?? s.bgColor : s.bgColor) || "6cc5ff";
  const hex = rawHex.replace("#", "");
  const r = Number.parseInt(hex.slice(0, 2), 16) || 0;
  const g = Number.parseInt(hex.slice(2, 4), 16) || 0;
  const b = Number.parseInt(hex.slice(4, 6), 16) || 0;
  const bg = `rgba(${r}, ${g}, ${b}, 0.4)`;
  const radius = Number((s as any).cornerRadius ?? 10);
  return {
    backgroundColor: bg,
    borderRadius: `${radius}px`,
    backdropFilter: useAuto ? "blur(40px)" : "none",
    filter: useAuto ? "saturate(1.25)" : "none",
    WebkitBackdropFilter: useAuto ? "blur(40px)" : "none",
  };
});

const baseMessageCardStyle = {
  backgroundColor: "#ffffff",
  borderRadius: "8px",
  paddingTop: "32px",
  paddingRight: "16px",
  paddingBottom: "32px",
  paddingLeft: "16px",
  width: "100%",
  maxWidth: "384px",
};

const messagePreviewCardStyle = computed(() => {
  const base = { ...baseMessageCardStyle };
  // Always apply custom styling in the preview
  return {
    ...base,
    ...messageContainerBoxStyle.value,
  };
});

const blurRepaintKey = ref(0);
watch(
  () => [
    (appStore.submitMessageStyle as any).cornerRadius,
    appStore.submitMessageStyle.bgColor,
    (appStore.submitMessageStyle as any).mobileBgColor,
    (appStore.submitMessageStyle as any).autoBgFromImage,
  ],
  () => {
    blurRepaintKey.value += 1;
  },
  { deep: true }
);

/**
 * Gets the active background image URL based on device mode
 * @returns {string} The background image URL
 */
const getCurrentBgUrl = (): string => {
  return getEffectiveBackgroundImageUrlForDevice(
    appStore.formStyle,
    appStore.deviceMode
  ).trim();
};

/**
 * Recomputes the background color from the current background image
 * @returns {Promise<void>}
 */
const recomputeBackgroundColor = async (): Promise<void> => {
  const isColorLocked = Boolean((appStore.submitMessageStyle as any).autoBgFromImageColorLocked);
  if (isColorLocked) {
    return;
  }
  const bgUrl = getCurrentBgUrl();
  if (!bgUrl) {
    // No-op if no bg image as per requirement
    return;
  }
  const useAuto = Boolean((appStore.submitMessageStyle as any).autoBgFromImage);
  if (!useAuto) {
    // Only compute if autoBgFromImage is enabled
    return;
  }
  try {
    const color = await computeDominantColorFromImage(bgUrl);
    const newColor = color.replace("#", "");

    // Get current color based on device mode
    const currentColor = isMobile.value
      ? (appStore.submitMessageStyle as any).mobileBgColor ?? appStore.submitMessageStyle.bgColor
      : appStore.submitMessageStyle.bgColor;

    // Only update and mark as changed if color actually changed
    if (newColor === currentColor) {
      return;
    }

    // Persist hex only (alpha handled in preview)
    if (isMobile.value) {
      (appStore.submitMessageStyle as any).mobileBgColor = newColor;
    } else {
      appStore.submitMessageStyle.bgColor = newColor;
    }
    appStore.anyUnsavedChanges = true;
    appStore.addToHistory();
  } catch (error) {
    console.error("Error computing dominant color from image:", error);
  }
};

// Recompute background color when relevant properties change
watch(
  () => [
    appStore.formStyle?.bgImage,
    appStore.formStyle?.mobileBgImage,
    appStore.formStyle?.mobileBgImageDeleted,
    appStore.deviceMode,
    (appStore.submitMessageStyle as any).autoBgFromImage,
  ],
  async () => {
    await recomputeBackgroundColor();
  },
  { immediate: true }
);

const previewBackgroundStyle = computed(() => {
  const trimmed = getEffectiveBackgroundImageUrlForDevice(
    appStore.formStyle,
    appStore.deviceMode
  ).trim();
  if (trimmed) {
    return {
      backgroundImage: `url('${trimmed}')`,
      backgroundSize: "cover",
      backgroundPosition: "center",
      backgroundColor: "transparent",
    };
  }
  return {
    backgroundColor: "#f2f4f7",
  };
});

interface Props {
  messageContent: string;
}

defineProps<Props>();
</script>

<template>
  <div class="preview-container preview-container--message">
    <div class="mb-2 text-sm font-medium text-gray-600">
      {{ t("common.preview") }}
    </div>
    <div
      class="bg-container relative flex min-h-[232px] w-full items-center justify-center overflow-hidden rounded-xl px-8 py-4"
      :style="previewBackgroundStyle"
    >
      <div
        class="relative z-10 flex w-full max-w-[384px] flex-col items-center justify-center overflow-hidden rounded-lg text-center"
        :style="messagePreviewCardStyle"
      >
        <!-- Overlay to simulate overall opacity and keep text opaque -->
        <div
          v-if="isAutoBgEnabled"
          :key="blurRepaintKey"
          class="pointer-events-none absolute inset-0 z-0 rounded-lg"
          :style="messageContainerOverlayStyle"
        />
        <div
          class="message-content relative z-[1] break-words text-center"
          :style="previewTextStyle"
          v-html="messageContent"
        ></div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.preview-container {
  height: auto;
  padding-bottom: 8px;
}

.message-content {
  word-wrap: break-word;
  overflow-wrap: break-word;
  word-break: break-word;
  hyphens: auto;
  display: -webkit-box;
  line-clamp: 4;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.message-content :deep(p) {
  margin-bottom: 0;
}

.bg-container {
  position: relative;
  height: 100%;
}
</style>
